import { useState } from "react";
import { HomeframeSelect } from "@builtbyted/homeframe";
import { api } from "./api";
import { count, useData, Message, Skeleton } from "./ui";

interface Match {
  usernames: [string, string];
  suggested_name: string;
  same_email: boolean;
  same_device: number;
  similar_names: boolean;
  nearby_plays: number;
  shared_networks: number;
  closest_seconds: number | null;
}
interface IdentityData {
  automatic_count: number;
  links: { alias: string; canonical: string }[];
  matches: Match[];
}
export function ViewerMatches({
  onChange,
  readOnly = false,
}: {
  onChange: () => void;
  readOnly?: boolean;
}) {
  const data = useData<IdentityData>("/user-identities");
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [busy, setBusy] = useState(false);
  const change = async (action: string, alias: string, canonical = "") => {
    setBusy(true);
    setError("");
    try {
      await api("/user-identities", { action, alias, canonical });
      setNotice(
        action === "merge"
          ? `Combined ${alias} with ${canonical}.`
          : action === "unlink"
            ? `Removed the link for ${alias}.`
            : "These viewers will stay separate.",
      );
      data.refresh();
      onChange();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="viewer-matches">
      {(error || data.error) && <Message error>{error || data.error}</Message>}
      {notice && (
        <div className="identity-notice" role="status">
          {notice}
        </div>
      )}
      {!data.data && !data.error && <Skeleton kind="cards" count={2} />}
      {data.data && (
        <p className="match-count">
          {count(data.data.matches.length)} possible matches
        </p>
      )}
      <div className="identity-grid">
        {data.data?.matches.map((match) => (
          <MatchCard
            key={match.usernames.join(":")}
            match={match}
            busy={busy}
            readOnly={readOnly}
            change={change}
          />
        ))}
      </div>
      {!!data.data?.links.length && (
        <div className="identity-links">
          <h3>Names you combined</h3>
          {data.data.links.map((link) => (
            <div key={link.alias}>
              <span>
                {link.alias} → <strong>{link.canonical}</strong>
              </span>
              {!readOnly && (
                <button
                  disabled={busy}
                  onClick={() => void change("unlink", link.alias)}
                >
                  Undo link
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
function MatchCard({
  match,
  busy,
  change,
  readOnly,
}: {
  match: Match;
  busy: boolean;
  readOnly: boolean;
  change: (action: string, alias: string, canonical: string) => Promise<void>;
}) {
  const [canonical, setCanonical] = useState(match.suggested_name);
  const alias = match.usernames.find((name) => name !== canonical)!;
  return (
    <article className="identity-card">
      <h3>
        {match.usernames[0]} <span>&</span> {match.usernames[1]}
      </h3>
      <div className="identity-evidence">
        {match.same_email && <span>Same email address</span>}
        {match.same_device > 0 && <span>Same playback device</span>}
        {match.similar_names && <span>Similar usernames</span>}
        {match.shared_networks > 0 && (
          <span>
            {count(match.shared_networks)} shared{" "}
            {match.shared_networks === 1 ? "IP" : "IPs"}
          </span>
        )}
      </div>
      {match.closest_seconds !== null && (
        <p>
          Playback {Math.max(1, Math.round(match.closest_seconds / 60))} min
          apart · {count(match.nearby_plays)} nearby plays
        </p>
      )}
      {!readOnly && (
        <>
          <label>
            Combine under
            <HomeframeSelect
              aria-label={`Combined name for ${match.usernames.join(" and ")}`}
              value={canonical}
              onChange={(e) => setCanonical(e.target.value)}
            >
              {match.usernames.map((name) => (
                <option value={name} key={name}>
                  {name}
                </option>
              ))}
            </HomeframeSelect>
          </label>
          <div className="identity-actions">
            <button
              className="primary-button"
              disabled={busy}
              onClick={() => void change("merge", alias, canonical)}
            >
              Combine viewers
            </button>
            <button
              disabled={busy}
              onClick={() => void change("dismiss", alias, canonical)}
            >
              Keep separate
            </button>
          </div>
        </>
      )}
    </article>
  );
}
